import { FragmentType, getFragmentData } from '@/__generated__';
import { AddressForm } from '@/features/address/components/AddressForm';
import { useMutation } from '@apollo/client';
import { ActionIcon, Alert, Group, Paper, Select, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEdit } from '@tabler/icons-react';
import { orderAddressDetailsFragment, orderCustomerDetailsFragment } from '../graphql/fragments';
import { upsertOrderAddressDocument } from '../graphql/mutations';

export const OrderAddressCard = ({
  orderId,
  ...props
}: {
  orderId: string;
  customer: FragmentType<typeof orderCustomerDetailsFragment>;
  address?: FragmentType<typeof orderAddressDetailsFragment> | null;
}) => {
  const customer = getFragmentData(orderCustomerDetailsFragment, props.customer);
  const address = getFragmentData(orderAddressDetailsFragment, props.address);

  const [upsertOrderAddress, { loading }] = useMutation(upsertOrderAddressDocument, {
    onCompleted: (data) => {
      const address = getFragmentData(orderAddressDetailsFragment, data.upsertOrderAddress.address);

      modals.closeAll();
      notifications.show({
        title: 'Order address updated',
        message: `The shipping address for the order has been successfully updated to: ${address?.name}, ${address?.streetLine}, ${address?.city}, ${address?.postCode}, ${address?.country}, ${address?.phone}`,
        color: 'teal',
      });
    },
  });

  const handleUpsert = () =>
    modals.open({
      title: 'Upsert address',
      children: (
        <AddressForm
          address={address}
          loading={loading}
          onSubmit={(input) =>
            void upsertOrderAddress({
              variables: {
                orderId,
                input,
              },
            })
          }
        />
      ),
    });

  return (
    <Paper p="md" withBorder>
      <Stack>
        <Group justify="space-between">
          <Text fw={500} size="lg">
            Adresa
          </Text>
          <ActionIcon onClick={handleUpsert}>
            <IconEdit size={16} />
          </ActionIcon>
        </Group>
        {address && (
          <Stack gap={0}>
            {(!/^[A-Za-zÀ-ž\s\-.]+?\s\d+([/-]?\w+)?$/.test(address.streetLine) ||
              /\b\d+([A-Za-z]|\/\d+|-\d+)?\b/.test(address.streetDescription ?? '')) && (
              <Alert color="red" my="md">
                Skontroluj adresu!
              </Alert>
            )}
            <Text>{address.name}</Text>
            <Text>{address.streetLine}</Text>
            {address.streetDescription && <Text> {address.streetDescription}</Text>}
            <Text>
              {address.postCode} {address.city}
            </Text>
            <Text>
              {address.country} {address.phone}
            </Text>
          </Stack>
        )}
        <Select
          data={(customer.addresses ?? []).map((address) => ({
            value: address.id,
            label: `${address.name}, ${address.streetLine}, ${address.streetDescription}, ${address.city}, ${address.postCode}, ${address.country}, ${address.phone}`,
          }))}
          value={address?.addressId}
          onChange={async (addressId) => {
            if (!addressId) return;

            const customerAddress = customer.addresses?.find((address) => address.id === addressId);

            if (!customerAddress) return;

            await upsertOrderAddress({
              variables: {
                orderId,
                input: { ...customerAddress, address: { connect: { id: addressId } } },
              },
            });
          }}
        />
      </Stack>
    </Paper>
  );
};
